import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  TextField,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import GroupsIcon from "@mui/icons-material/Groups";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SendIcon from "@mui/icons-material/Send";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import subMonths from "date-fns/subMonths";
import startOfMonth from "date-fns/startOfMonth";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function getGroupClientsUrl() {
  const raw = process.env.REACT_APP_GET_GROUP_CLIENTS;
  const url = (typeof raw === "string" ? raw : "").replace(/^['"]|['"]$/g, "").trim();
  return url;
}

function getGroupInvoiceUrl() {
  const raw = process.env.REACT_APP_GET_GROUP_INVOICE;
  const url = (typeof raw === "string" ? raw : "").replace(/^['"]|['"]$/g, "").trim();
  return url;
}

function getPostGroupInvoiceUrl() {
  const raw = process.env.REACT_APP_POST_GROUP_INVOICE;
  return (typeof raw === "string" ? raw : "").replace(/^['"]|['"]$/g, "").trim();
}

/** Group flat API response (client_fk, client_name, group_fk, group_name, invoice_num, start_date, end_date, cost) by group */
function groupInvoiceResponseByGroup(rawList) {
  if (!Array.isArray(rawList) || rawList.length === 0) return [];
  const byGroup = new Map();
  for (const item of rawList) {
    const key = item.group_fk ?? item.group_name ?? "unknown";
    if (!byGroup.has(key)) {
      byGroup.set(key, {
        group_fk: item.group_fk,
        group_name: item.group_name?.trim() ?? "",
        clients: [],
      });
    }
    byGroup.get(key).clients.push(item);
  }
  return Array.from(byGroup.values());
}

function formatInvoiceDate(isoStr) {
  if (!isoStr) return "-";
  try {
    const d = new Date(isoStr);
    if (Number.isNaN(d.getTime())) return isoStr;
    const day = String(d.getDate()).padStart(2, "0");
    const month = months[d.getMonth()]?.slice(0, 3) || String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return isoStr;
  }
}

function GroupRow({ group, clientsData }) {
  const [open, setOpen] = useState(false);

  const groupClients = clientsData?.filter(
    (c) => c.group_fk === group.group_fk
  );

  return (
    <React.Fragment key={group.group_fk}>
      <TableRow
        className="group-invoice-tbody"
        sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#e8f4fd" } }}
      >
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            onClick={() => setOpen((prev) => !prev)}
            disabled={!groupClients?.length}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell
          onClick={() => setOpen((prev) => !prev)}
          sx={{ fontWeight: 500, fontSize: "13px" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <GroupsIcon sx={{ color: "#1e40af", fontSize: "18px" }} />
            {group.group_name?.trim()}
          </div>
        </TableCell>
        <TableCell
          onClick={() => setOpen((prev) => !prev)}
          sx={{ textAlign: "center" }}
        >
          <Chip
            label={group.total_clients}
            size="small"
            icon={<PeopleAltIcon style={{ fontSize: "14px" }} />}
            sx={{
              backgroundColor: "#dbeafe",
              color: "#1e40af",
              fontWeight: 600,
              fontSize: "12px",
            }}
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={3} sx={{ padding: 0, borderBottom: "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <div style={{ backgroundColor: "#f0f7ff", padding: "8px 16px 16px 48px" }}>
              <Typography
                variant="subtitle2"
                sx={{ color: "#1e40af", mb: 1, fontWeight: 600 }}
              >
                Clients in {group.group_name?.trim()}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#1e40af" }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#ffffff" }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#ffffff" }}>
                      Client Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#ffffff", textAlign: "center" }}>
                      Client ID
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#ffffff", textAlign: "center" }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupClients?.length > 0 ? (
                    groupClients.map((client, idx) => (
                      <TableRow
                        key={client.client_fk}
                        sx={{
                          "&:hover": { backgroundColor: "#dbeafe" },
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8faff",
                        }}
                      >
                        <TableCell sx={{ fontSize: "12px", color: "#64748b", width: "40px" }}>
                          {idx + 1}
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          {client.client_name}
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>
                          {client.client_fk}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          <Chip
                            label={client.client_status}
                            size="small"
                            sx={{
                              backgroundColor:
                                client.client_status === "ACTIVE"
                                  ? "#dcfce7"
                                  : "#fee2e2",
                              color:
                                client.client_status === "ACTIVE"
                                  ? "#166534"
                                  : "#991b1b",
                              fontWeight: 600,
                              fontSize: "11px",
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        sx={{ textAlign: "center", color: "#94a3b8", py: 2 }}
                      >
                        No clients found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function GroupClientsTab() {
  const [groupData, setGroupData] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchGroupClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = getGroupClientsUrl();
      const res = await axios.get(url);
      const data = res?.data;
      setGroupData(data?.group_client_data || []);
      setClientsData(data?.clients_data || []);
    } catch (err) {
      console.error("Error fetching group clients:", err);
      setError(err.message || "Failed to load group clients. If it works in Postman, the server may need to allow CORS for this origin.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupClients();
  }, []);

  const filteredGroups = groupData.filter((g) =>
    g.group_name?.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedGroups = filteredGroups.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filteredGroups.length) {
      setPage(0);
    }
  }, [filteredGroups.length, page, rowsPerPage]);

  return (
    <div style={{ padding: "16px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <Typography variant="body2" sx={{ color: "#64748b" }}>
          {filteredGroups.length} group{filteredGroups.length !== 1 ? "s" : ""} found
        </Typography>
        <TextField
          size="small"
          placeholder="Search group..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: "240px" }}
        />
      </div>

      {error && (
        <div
          style={{
            padding: "12px 16px",
            marginBottom: "16px",
            backgroundColor: "#fef2f2",
            color: "#991b1b",
            borderRadius: "8px",
            fontSize: "13px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{error}</span>
          <Button size="small" onClick={fetchGroupClients} sx={{ textTransform: "none" }}>
            Retry
          </Button>
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <CircularProgress size={36} />
        </div>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ boxShadow: "0 1px 4px rgba(0,0,0,0.1)", borderRadius: "8px", maxHeight: "calc(100vh - 310px)", overflow: "auto" }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ width: "48px", backgroundColor: "#1e40af", padding: "10px 8px" }}
                />
                <TableCell
                  sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px" }}
                >
                  Group Name
                </TableCell>
                <TableCell
                  sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center", width: "160px" }}
                >
                  Total Clients
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGroups.length > 0 ? (
                paginatedGroups.map((group) => (
                  <GroupRow
                    key={`group-${group.group_fk}`}
                    group={group}
                    clientsData={clientsData}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{ textAlign: "center", py: 4, color: "#94a3b8" }}
                  >
                    No groups found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 15, 20, 50]}
            component="div"
            count={filteredGroups.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: "1px solid #e2e8f0" }}
          />
        </TableContainer>
      )}
    </div>
  );
}

function GroupInvoiceTab() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchMonth, setSearchMonth] = useState(null);
  const [invoiceData, setInvoiceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const initDate = () => {
    const initialDate = startOfMonth(subMonths(new Date(), 1));
    setSelectedDate(dayjs(initialDate.toString()));
    const d = new Date();
    const prevMonth = d.getMonth() === 0 ? 11 : d.getMonth() - 1;
    const prevYear = d.getMonth() === 0 ? d.getFullYear() - 1 : d.getFullYear();
    setSearchMonth(`${months[prevMonth]} ${prevYear}`);
  };

  const fetchGroupInvoice = async (month) => {
    if (!month) return;
    setLoading(true);
    try {
      const date = new Date(month);
      const y = date.getFullYear();
      const m = date.getMonth() + 1;
      if (y === 1970) return;
      const monthParam = `${y}-${String(m).padStart(2, "0")}`;
      const baseUrl = getGroupInvoiceUrl();
      const url = `${baseUrl}?month=${monthParam}`;
      const res = await axios.get(url);
      const data = res?.data;
      const rawList = Array.isArray(data) ? data : (data?.group_invoice_data || []);
      setInvoiceData(groupInvoiceResponseByGroup(rawList));
    } catch (err) {
      console.error("Error fetching group invoice:", err);
      setInvoiceData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initDate();
  }, []);

  useEffect(() => {
    if (searchMonth) {
      fetchGroupInvoice(searchMonth);
    }
  }, [searchMonth]);

  const handleDateChange = (e) => {
    const monthYear = `${months[e.$d.getMonth()]} ${e.$d.getFullYear()}`;
    setSearchMonth(monthYear);
    setSelectedDate(e);
  };

  const filteredData = invoiceData.filter((row) =>
    row.group_name?.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filteredData.length) {
      setPage(0);
    }
  }, [filteredData.length, page, rowsPerPage]);

  const getClientInvoices = (row) => {
    return row?.clients || row?.invoices || row?.client_invoices || [];
  };

  /** Only clients that have both invoice number and amount (cost) */
  const getClientsWithInvoiceAndAmount = (row) => {
    const clients = getClientInvoices(row);
    return clients.filter(
      (c) =>
        c.invoice_num != null &&
        String(c.invoice_num).trim() !== "" &&
        c.cost != null &&
        !Number.isNaN(Number(c.cost))
    );
  };

  const getNoOfInvoices = (row) => {
    return getClientsWithInvoiceAndAmount(row).length;
  };

  const getSumOfCost = (row) => {
    const withAmount = getClientsWithInvoiceAndAmount(row);
    if (withAmount.length === 0) return 0;
    return withAmount.reduce((sum, c) => sum + Number(c.cost), 0);
  };

  const handleRowClick = (row, idx) => (e) => {
    const key = row.group_fk ?? `row-${idx}`;
    setExpandedRow((prev) => (prev === key ? null : key));
  };

  const rowKey = (row, idx) => row.group_fk ?? `row-${idx}`;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedGroups(new Set(filteredData.map((row, idx) => rowKey(row, idx))));
    } else {
      setSelectedGroups(new Set());
    }
  };

  const handleSelectRow = (row, idx) => (e) => {
    e.stopPropagation();
    const key = rowKey(row, idx);
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allSelected = filteredData.length > 0 && selectedGroups.size === filteredData.length;
  const someSelected = selectedGroups.size > 0;

  const selectedList = filteredData.filter((row, idx) => selectedGroups.has(rowKey(row, idx)));

  const handleSendGroupInvoice = (e) => {
    e?.stopPropagation();
    setConfirmOpen(true);
    setSendError(null);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setSendError(null);
  };

  const handleConfirmSend = async () => {
    const baseUrl = getPostGroupInvoiceUrl();
    if (!baseUrl) {
      setSendError("Send group invoice URL is not configured.");
      return;
    }
    if (!selectedDate) {
      setSendError("Please select a month and year.");
      return;
    }
    const group_fk = selectedList.map((row) => row.group_fk);
    const month = selectedDate.month() + 1;
    const year = selectedDate.year();
    setSending(true);
    setSendError(null);
    try {
      const res = await axios.post(baseUrl, { group_fk, month, year }, {
        headers: { "Content-Type": "application/json" },
      });
      if (!(res?.status >= 200 && res?.status < 300)) {
        throw new Error(`HTTP ${res?.status}`);
      }
      setConfirmOpen(false);
      setSelectedGroups(new Set());
      setSuccessOpen(true);
    } catch (err) {
      console.error("Error sending group invoice:", err);
      const msg = err?.response?.data
        ? (typeof err.response.data === "string" ? err.response.data : err.response.data?.message)
        : err?.message;
      setSendError(msg || "Failed to send group invoice.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ padding: "16px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              views={["month", "year"]}
              label="Select Month and Year"
              value={selectedDate}
              onChange={handleDateChange}
              slotProps={{
                textField: { size: "small", sx: { width: "200px" } },
              }}
            />
          </LocalizationProvider>
          {searchMonth && (
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Showing: <strong>{searchMonth}</strong>
            </Typography>
          )}
        </div>
        <TextField
          size="small"
          placeholder="Search group..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: "240px" }}
        />
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSendGroupInvoice}
          disabled={!someSelected}
          sx={{
            textTransform: "none",
            fontSize: "14px",
            backgroundColor: "#1e40af",
            "&:hover": { backgroundColor: "#1d4ed8" },
          }}
        >
          Send Group Invoice
        </Button>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <CircularProgress size={36} />
        </div>
      ) : (
        <>
        <TableContainer
          component={Paper}
          sx={{ boxShadow: "0 1px 4px rgba(0,0,0,0.1)", borderRadius: "8px", maxHeight: "calc(100vh - 310px)", overflow: "auto" }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: "48px", backgroundColor: "#1e40af", padding: "10px 8px" }}>
                  <Checkbox
                    indeterminate={someSelected && !allSelected}
                    checked={allSelected}
                    onChange={handleSelectAll}
                    size="small"
                    sx={{ color: "#fff", "&.Mui-checked": { color: "#fff" } }}
                  />
                </TableCell>
                <TableCell sx={{ width: "40px", backgroundColor: "#1e40af", padding: "10px 8px" }} />
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px" }}>
                  Group Name
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>
                  No of Invoices
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>
                  Sum of Cost
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                paginatedData.map((row, i) => {
                  const idx = page * rowsPerPage + i;
                  const key = row.group_fk ?? `row-${idx}`;
                  const isOpen = expandedRow === key;
                  const clientInvoices = getClientsWithInvoiceAndAmount(row);
                  return (
                    <React.Fragment key={key}>
                      <TableRow
                        sx={{
                          "&:hover": { backgroundColor: "#e8f4fd" },
                          backgroundColor: isOpen ? "#e0f2fe" : idx % 2 === 0 ? "#ffffff" : "#f8faff",
                          cursor: "pointer",
                        }}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()} sx={{ padding: "8px" }}>
                          <Checkbox
                            size="small"
                            checked={selectedGroups.has(key)}
                            onChange={handleSelectRow(row, idx)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell
                          padding="checkbox"
                          onClick={handleRowClick(row, idx)}
                          sx={{ padding: "8px" }}
                        >
                          <IconButton size="small" aria-label={isOpen ? "collapse" : "expand"}>
                            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell
                          onClick={handleRowClick(row, idx)}
                          sx={{ fontSize: "12px", fontWeight: 500 }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <GroupsIcon sx={{ color: "#1e40af", fontSize: "16px" }} />
                            {row.group_name?.trim()}
                          </div>
                        </TableCell>
                        <TableCell
                          onClick={handleRowClick(row, idx)}
                          sx={{ fontSize: "12px", textAlign: "center" }}
                        >
                          {getNoOfInvoices(row)}
                        </TableCell>
                        <TableCell
                          onClick={handleRowClick(row, idx)}
                          sx={{ fontSize: "12px", textAlign: "center" }}
                        >
                          {getSumOfCost(row) != null
                            ? `₹${Number(getSumOfCost(row)).toLocaleString("en-IN")}`
                            : "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={5} sx={{ padding: 0, borderBottom: "none", verticalAlign: "top" }}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <div style={{ backgroundColor: "#f0f7ff", padding: "12px 16px 16px 48px", borderLeft: "3px solid rgb(3, 20, 78)" }}>
                              <Typography variant="subtitle2" sx={{ color: "#1e40af", mb: 1.5, fontWeight: 600 }}>
                                Client invoice details – {row.group_name?.trim()}
                              </Typography>
                              <Table size="small" sx={{ backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden", boxShadow: "0 1px 3px rgba(26, 6, 83, 0.08)" }}>
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: "#1e40af" }}>
                                    <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#ffffff" }}>Client Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#ffffff", textAlign: "center" }}>Invoice Number</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#ffffff", textAlign: "center" }}>Start Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#ffffff", textAlign: "center" }}>End Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#ffffff", textAlign: "center" }}>Invoice Month</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#ffffff", textAlign: "center" }}>Invoice Amount</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {clientInvoices.length > 0 ? (
                                    clientInvoices.map((inv, i) => (
                                      <TableRow
                                        key={inv.client_fk ?? inv.invoice_num ?? i}
                                        sx={{
                                          "&:hover": { backgroundColor: "#dbeafe" },
                                          backgroundColor: i % 2 === 0 ? "#ffffff" : "#f8faff",
                                        }}
                                      >
                                        <TableCell sx={{ fontSize: "12px" }}>{inv.client_name ?? "-"}</TableCell>
                                        <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{inv.invoice_num ?? "-"}</TableCell>
                                        <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{formatInvoiceDate(inv.start_date)}</TableCell>
                                        <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{formatInvoiceDate(inv.end_date)}</TableCell>
                                        <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{searchMonth ?? formatInvoiceDate(inv.start_date) ?? "-"}</TableCell>
                                        <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>
                                          {inv.cost != null ? `₹${Number(inv.cost).toLocaleString("en-IN")}` : "-"}
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  ) : (
                                    <TableRow>
                                      <TableCell colSpan={6} sx={{ textAlign: "center", py: 2, color: "#94a3b8", fontSize: "12px" }}>
                                        No client invoice details available
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </div>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                      <GroupsIcon sx={{ fontSize: "40px", color: "#cbd5e1" }} />
                      <Typography variant="body2">
                        No group invoice data found for {searchMonth}
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 15, 20, 50]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: "1px solid #e2e8f0" }}
          />
        </TableContainer>
        </>
      )}

      <Dialog open={confirmOpen} onClose={handleCloseConfirm} maxWidth="md" fullWidth>
        <DialogTitle>Confirm Send Group Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to send group invoice for the following {selectedList.length} group(s) for <strong>{searchMonth}</strong>:
          </Typography>
          <Table size="small" sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1e40af" }}>
                <TableCell sx={{ color: "#fff", fontWeight: 600, fontSize: "12px" }}>Group Name</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600, fontSize: "12px", textAlign: "center" }}>No of Invoices</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600, fontSize: "12px", textAlign: "center" }}>Sum of Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedList.map((row) => (
                <TableRow key={row.group_fk}>
                  <TableCell sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>{row.group_name ?? "-"}</TableCell>
                  <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{getNoOfInvoices(row)}</TableCell>
                  <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>
                    {getSumOfCost(row) != null ? `₹${Number(getSumOfCost(row)).toLocaleString("en-IN")}` : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="body2" sx={{ mt: 2, color: "#64748b" }}>
            Do you want to proceed?
          </Typography>
          {sendError && (
            <Typography variant="body2" sx={{ mt: 1, color: "#dc2626" }}>
              {sendError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseConfirm} color="inherit" disabled={sending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmSend} disabled={sending} sx={{ backgroundColor: "#1e40af", "&:hover": { backgroundColor: "#1d4ed8" } }}>
            {sending ? "Sending..." : "Confirm & Send"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={successOpen} autoHideDuration={4000} onClose={() => setSuccessOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: "100%" }}>
          Group invoice sent successfully.
        </Alert>
      </Snackbar>
    </div>
  );
}

function GroupInvoice({ isSidebarOpen }) {
  const [activeTab, setActiveTab] = useState("group_clients");

  const tabs = [
    { id: "group_clients", label: "Group Clients" },
    { id: "group_invoice", label: "Group Invoice" },
  ];

  return (
    <div style={{ padding: "0" }}>
      <div
        style={{
          display: "flex",
          borderBottom: "2px solid #e2e8f0",
          marginBottom: "4px",
        }}
      >
        {tabs.map((tab) => (
          <div
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 24px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? "#1e40af" : "#64748b",
              borderBottom: activeTab === tab.id ? "2px solid #1e40af" : "2px solid transparent",
              marginBottom: "-2px",
              transition: "all 0.15s",
              userSelect: "none",
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>

      <div style={{ paddingTop: "4px" }}>
        {activeTab === "group_clients" && <GroupClientsTab />}
        {activeTab === "group_invoice" && <GroupInvoiceTab />}
      </div>
    </div>
  );
}

export default GroupInvoice;
